import { PanelRightClose } from 'lucide-react'
import { useSidebarTheme } from '@/contexts/sidebar-theme-context'

interface SettingsPanelProps {
  isOpen: boolean
  onClose: () => void
  headerHeight?: number
}

export function SettingsPanel({ isOpen, onClose, headerHeight = 72 }: SettingsPanelProps) {
  const { theme, setTheme, color, setColor } = useSidebarTheme()

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={onClose}
        />
      )}

      {/* Settings Panel */}
      <div
        className={`fixed right-0 w-96 bg-white border-l border-gray-200 shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
      >
        <div className="p-8">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 m-0 p-0">Navigation Settings</h2>
              <p className="text-sm text-gray-500 mt-1">Configure your preferences.</p>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
              title="Close panel"
            >
              <PanelRightClose className="h-5 w-5 text-gray-700" />
            </button>
          </div>

          <div className="mt-8 space-y-8">
            <div>
              <h3 className="text-base font-bold text-gray-800 p-0 m-0">Sidebar Style</h3>
              <p className="text-sm text-gray-500 mb-4">Choose theme colour.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setTheme('dark')}
                  className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-white'
                      : 'border-2 border-gray-300 text-gray-700 hover:bg-gray-800 hover:text-white hover:border-gray-800'
                  }`}
                >
                  dark
                </button>
                <button
                  onClick={() => setTheme('original')}
                  className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    theme === 'original'
                      ? 'text-white'
                      : 'border-2 border-gray-300 text-gray-700 hover:text-white hover:border-[hsl(142,70%,35%)]'
                  }`}
                  style={theme === 'original' ? { backgroundColor: 'hsl(142, 70%, 35%)' } : undefined}
                  onMouseEnter={(e) => {
                    if (theme !== 'original') {
                      e.currentTarget.style.backgroundColor = 'hsl(142, 70%, 35%)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (theme !== 'original') {
                      e.currentTarget.style.backgroundColor = ''
                    }
                  }}
                >
                  original
                </button>
                <button
                  onClick={() => setTheme('white')}
                  className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    theme === 'white'
                      ? 'bg-gray-800 text-white'
                      : 'border-2 border-gray-300 text-gray-700 hover:bg-gray-800 hover:text-white hover:border-gray-800'
                  }`}
                >
                  white
                </button>
              </div>
            </div>

            <div className={theme === 'original' ? 'opacity-50 pointer-events-none' : ''}>
              <h3 className="text-base font-bold text-gray-800 p-0 mt-8 m-0">Sidebar Menu Colours</h3>
              <p className="text-sm text-gray-500 mb-4 p-0 m-0">Choose menu colour.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setColor('pink')}
                  className={`w-10 h-10 rounded-md bg-pink-500 hover:ring-2 ring-gray-300 ${color === 'pink' ? 'ring-2 ring-gray-800' : ''}`}
                  disabled={theme === 'original'}
                />
                <button
                  onClick={() => setColor('gray')}
                  className={`w-10 h-10 rounded-md bg-gray-800 hover:ring-2 ring-gray-300 ${color === 'gray' ? 'ring-2 ring-gray-400' : ''}`}
                  disabled={theme === 'original'}
                />
                <button
                  onClick={() => setColor('blue')}
                  className={`w-10 h-10 rounded-md bg-green-500 hover:ring-2 ring-gray-300 ${color === 'blue' ? 'ring-2 ring-gray-800' : ''}`}
                  disabled={theme === 'original'}
                />
                <button
                  onClick={() => setColor('green')}
                  className={`w-10 h-10 rounded-md bg-green-500 hover:ring-2 ring-gray-300 ${color === 'green' ? 'ring-2 ring-gray-800' : ''}`}
                  disabled={theme === 'original'}
                />
                <button
                  onClick={() => setColor('orange')}
                  className={`w-10 h-10 rounded-md bg-orange-500 hover:ring-2 ring-gray-300 ${color === 'orange' ? 'ring-2 ring-gray-800' : ''}`}
                  disabled={theme === 'original'}
                />
                <button
                  onClick={() => setColor('red')}
                  className={`w-10 h-10 rounded-md bg-red-500 hover:ring-2 ring-gray-300 ${color === 'red' ? 'ring-2 ring-gray-800' : ''}`}
                  disabled={theme === 'original'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
